import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Phone, ArrowRight, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { toast } from 'sonner';

const LoginPage = () => {
  const { login: googleLogin, loginWithCredentials, register } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerPhone, setRegisterPhone] = useState('');

  // Nova função para Google Login com verificação de telefone
  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const userData = await googleLogin();
      toast.success(language === 'pt' ? 'Login realizado com sucesso!' : 'Login successful!');
      
      // Verificação crucial: se não tiver telefone, vai para profile
      if (!userData?.phone) {
        navigate('/profile');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Google login error:', err);
      setError(language === 'pt' ? 'Erro ao fazer login com Google.' : 'Google login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const userData = await loginWithCredentials(loginEmail, loginPassword);
      toast.success(language === 'pt' ? 'Login realizado com sucesso!' : 'Login successful!');
      
      // Verificação de telefone adicionada aqui
      if (!userData?.phone) {
        navigate('/profile');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMsg = err.response?.data?.detail;
      
      if (errorMsg === 'Invalid email or password') {
        setError(language === 'pt' ? 'Email ou senha incorretos' : 'Invalid email or password');
      } else if (errorMsg === 'This account uses Google login.') {
        setError(language === 'pt' ? 'Esta conta usa login pelo Google. Clique em "Continuar com Google".' : 'This account uses Google login. Click "Continue with Google".');
      } else {
        setError(errorMsg || (language === 'pt' ? 'Erro ao fazer login. Tente novamente.' : 'Login failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    // Validations
    if (registerPassword.length < 6) {
      setError(language === 'pt' ? 'A senha deve ter pelo menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }

    if (registerPassword !== registerConfirmPassword) {
      setError(language === 'pt' ? 'As senhas não coincidem' : 'Passwords do not match');
      return;
    }

    if (!registerName.trim()) {
      setError(language === 'pt' ? 'Por favor, insira o seu nome' : 'Please enter your name');
      return;
    }

    setLoading(true);
    
    try {
      const userData = await register({
        name: registerName.trim(),
        email: registerEmail.toLowerCase().trim(),
        password: registerPassword,
        phone: registerPhone.trim() || null,
      });
      toast.success(language === 'pt' ? 'Conta criada com sucesso!' : 'Account created successfully!');
      
      // No registro, se o campo opcional phone estiver vazio, manda para profile
      if (!userData?.phone && !registerPhone.trim()) {
        navigate('/profile');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Register error:', err);
      const errorMsg = err.response?.data?.detail;
      
      if (errorMsg === 'Email already registered') {
        setError(language === 'pt' ? 'Este email já está registado. Tente fazer login.' : 'This email is already registered. Try logging in.');
      } else if (errorMsg?.includes('password')) {
        setError(language === 'pt' ? 'Senha inválida. Use pelo menos 6 caracteres.' : 'Invalid password. Use at least 6 characters.');
      } else {
        setError(errorMsg || (language === 'pt' ? 'Erro ao criar conta. Tente novamente.' : 'Failed to create account. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] pt-24 pb-16 flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md px-6">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in-up">
          <h1 className="font-heading text-3xl font-medium text-[#1A1815]">Lou's Way</h1>
          <p className="text-[#635E54] mt-2">
            {language === 'pt' ? 'Entre ou crie sua conta' : 'Sign in or create your account'}
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-white rounded-3xl p-8 shadow-lg border border-[#E6E2DA] animate-fade-in-up animation-delay-100">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Social Login */}
          <div className="space-y-3 mb-6">
            <Button
              onClick={handleGoogleLogin} // Alterado para a nova função
              variant="outline"
              className="w-full rounded-full py-6 border-[#E6E2DA] hover:border-[#BFA05F] hover:bg-[#F9F8F6]"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {language === 'pt' ? 'Continuar com Google' : 'Continue with Google'}
            </Button>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#E6E2DA]" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-[#A8A29A]">
                {language === 'pt' ? 'ou com email' : 'or with email'}
              </span>
            </div>
          </div>

          {/* Email/Password Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setError(''); }}>
            <TabsList className="w-full bg-[#F9F8F6] rounded-full p-1 mb-6">
              <TabsTrigger
                value="login"
                className="flex-1 rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-login"
              >
                {language === 'pt' ? 'Entrar' : 'Sign In'}
              </TabsTrigger>
              <TabsTrigger
                value="register"
                className="flex-1 rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="tab-register"
              >
                {language === 'pt' ? 'Criar Conta' : 'Sign Up'}
              </TabsTrigger>
            </TabsList>

            {/* Login Form */}
            <TabsContent value="login">
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29A]" />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="pl-12 rounded-full border-[#E6E2DA] focus:border-[#BFA05F] py-6"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29A]" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={language === 'pt' ? 'Senha' : 'Password'}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="pl-12 pr-12 rounded-full border-[#E6E2DA] focus:border-[#BFA05F] py-6"
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A29A] hover:text-[#635E54]"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                {/* Forgot Password Link */}
                <div className="text-right">
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-[#BFA05F] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    {language === 'pt' ? 'Esqueceu a senha?' : 'Forgot password?'}
                  </Link>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full py-6"
                  data-testid="login-submit"
                >
                  {loading ? (
                    <span className="animate-pulse">{language === 'pt' ? 'A entrar...' : 'Signing in...'}</span>
                  ) : (
                    <>
                      {language === 'pt' ? 'Entrar' : 'Sign In'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>

            {/* Register Form */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29A]" />
                    <Input
                      type="text"
                      placeholder={language === 'pt' ? 'Nome completo *' : 'Full name *'}
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      className="pl-12 rounded-full border-[#E6E2DA] focus:border-[#BFA05F] py-6"
                      required
                      data-testid="register-name"
                    />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29A]" />
                    <Input
                      type="email"
                      placeholder="Email *"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      className="pl-12 rounded-full border-[#E6E2DA] focus:border-[#BFA05F] py-6"
                      required
                      data-testid="register-email"
                    />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29A]" />
                    <Input
                      type="tel"
                      placeholder={language === 'pt' ? 'Telemóvel (opcional)' : 'Phone (optional)'}
                      value={registerPhone}
                      onChange={(e) => setRegisterPhone(e.target.value)}
                      className="pl-12 rounded-full border-[#E6E2DA] focus:border-[#BFA05F] py-6"
                      data-testid="register-phone"
                    />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29A]" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={language === 'pt' ? 'Senha (mín. 6 caracteres) *' : 'Password (min. 6 chars) *'}
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      className="pl-12 pr-12 rounded-full border-[#E6E2DA] focus:border-[#BFA05F] py-6"
                      required
                      minLength={6}
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A29A] hover:text-[#635E54]"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29A]" />
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder={language === 'pt' ? 'Confirmar senha *' : 'Confirm password *'}
                      value={registerConfirmPassword}
                      onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                      className="pl-12 pr-12 rounded-full border-[#E6E2DA] focus:border-[#BFA05F] py-6"
                      required
                      data-testid="register-confirm-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A29A] hover:text-[#635E54]"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {registerPassword && registerConfirmPassword && registerPassword !== registerConfirmPassword && (
                    <p className="text-xs text-red-500 mt-1 ml-4">
                      {language === 'pt' ? 'As senhas não coincidem' : 'Passwords do not match'}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={loading || (registerPassword !== registerConfirmPassword)}
                  className="w-full bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full py-6 disabled:opacity-50"
                  data-testid="register-submit"
                >
                  {loading ? (
                    <span className="animate-pulse">{language === 'pt' ? 'A criar conta...' : 'Creating account...'}</span>
                  ) : (
                    <>
                      {language === 'pt' ? 'Criar Conta' : 'Create Account'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        {/* Back to home */}
        <p className="text-center mt-6 text-sm text-[#635E54]">
          <button
            onClick={() => navigate('/')}
            className="hover:text-[#BFA05F] underline-offset-4 hover:underline"
          >
            {language === 'pt' ? '← Voltar à página inicial' : '← Back to home'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;